import { Currencies } from "@finway-group/shared/lib/consts"
import { CurrencyEnum } from "@finway-group/shared/lib/models"
import {
    LINE_ITEM_MATERIAL_CODE_MAX_LENGTH,
    LINE_ITEM_MATERIAL_CODE_MIN_LENGTH,
    LINE_ITEM_NAME_MAX_LENGTH,
    LINE_ITEM_NAME_MIN_LENGTH,
    LINE_ITEM_NOTE_MAX_LENGTH,
    LINE_ITEM_NOTE_MIN_LENGTH,
    LINE_ITEM_PRICE_MIN_BIGGER_THAN,
    LINE_ITEM_QUANTITY_MAX,
    LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES,
    LINE_ITEM_QUANTITY_MIN_BIGGER_THAN,
    LINE_ITEM_UNIT_OF_MEASURE_MAX_LENGTH,
    LINE_ITEM_UNIT_OF_MEASURE_MIN_LENGTH,
} from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { isAllowedCompanyNameCharsOnly, isValidUrl } from "@finway-group/shared/lib/utils/validators"
import { isAmountBelowCurrencyReasonableLimit } from "@finway-group/shared/lib/utils/validators/currencyLimit.validator"
import { isNumberWithinDecimalPlaces } from "@finway-group/shared/lib/utils/validators/input.validator"
import { Rule } from "antd/lib/form"
import { generalRules } from "~Components/forms/rules/common/general.rules"
import { stringRules } from "~Components/forms/rules/common/string.rules"
import i18n from "~Shared/locales/i18n"
import { isSet } from "~Shared/utils/helper.utils"

export const createLineItemRules = () => ({
    genericPrice: [
        ({ getFieldValue }) => ({
            validator: (_rule, value: number) => {
                if (!value) {
                    return Promise.reject(new Error(i18n.t("validation:required")))
                }
                if (value <= LINE_ITEM_PRICE_MIN_BIGGER_THAN) {
                    return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: LINE_ITEM_PRICE_MIN_BIGGER_THAN })))
                }

                const currencyValue = getFieldValue("currency") ?? CurrencyEnum.EUR

                if (!isAmountBelowCurrencyReasonableLimit(currencyValue, value)) {
                    return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: Currencies[currencyValue].max_allowed_value })))
                }

                return Promise.resolve()
            },
        }),
    ] as Array<Rule>,
    link: [
        stringRules.noExcessWhitespace,
        {
            validator: (_rule, value: string) => {
                if (!value) {
                    return Promise.resolve()
                }
                if (!isValidUrl(value)) {
                    return Promise.reject(new Error(i18n.t("validation:url")))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    materialCode: [
        stringRules.noExcessWhitespace,
        stringRules.minLength(LINE_ITEM_MATERIAL_CODE_MIN_LENGTH),
        stringRules.maxLength(LINE_ITEM_MATERIAL_CODE_MAX_LENGTH),
        stringRules.allowedCompanyNameCharsOnly,
    ] as Array<Rule>,
    name: [
        // keep line breaks
        generalRules.required,
        stringRules.noExcessWhitespace,
        stringRules.minLength(LINE_ITEM_NAME_MIN_LENGTH),
        stringRules.maxLength(LINE_ITEM_NAME_MAX_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    note: [
        stringRules.noExcessWhitespace,
        stringRules.minLength(LINE_ITEM_NOTE_MIN_LENGTH),
        stringRules.maxLength(LINE_ITEM_NOTE_MAX_LENGTH),
        stringRules.allowedCompanyNameCharsOnly,
    ] as Array<Rule>,
    quantity: [
        {
            required: true,
            type: "number",
            validator: (_rule, value: number) => {
                if (!value) {
                    return Promise.reject(new Error(i18n.t("validation:required")))
                }
                if (value <= LINE_ITEM_QUANTITY_MIN_BIGGER_THAN) {
                    return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: LINE_ITEM_QUANTITY_MIN_BIGGER_THAN })))
                }
                if (value > LINE_ITEM_QUANTITY_MAX) {
                    return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: LINE_ITEM_QUANTITY_MAX + 1 })))
                }
                if (!isNumberWithinDecimalPlaces(value, LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES)) {
                    return Promise.reject(new Error(i18n.t("validation:number_exceeding_decimal_places", { decimal_places: LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES })))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    taxPrice: [
        ({ getFieldValue }) => ({
            validator: (_rule, value: number) => {
                if (!isSet(value)) {
                    return Promise.reject(new Error(i18n.t("validation:required")))
                }

                if (value < LINE_ITEM_PRICE_MIN_BIGGER_THAN) {
                    return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: LINE_ITEM_PRICE_MIN_BIGGER_THAN })))
                }

                const currencyValue = getFieldValue("currency") ?? CurrencyEnum.EUR

                if (!isAmountBelowCurrencyReasonableLimit(currencyValue, value)) {
                    return Promise.reject(new Error(i18n.t("validation:below.number_short", { max: Currencies[currencyValue].max_allowed_value })))
                }

                return Promise.resolve()
            },
        }),
    ] as Array<Rule>,
    unitOfMeasure: [
        {
            validator: (_rule, value: string) => {
                if (!value) {
                    return Promise.resolve()
                }
                if (value.length < LINE_ITEM_UNIT_OF_MEASURE_MIN_LENGTH) {
                    return Promise.reject(new Error(i18n.t("validation:min.string", { min: LINE_ITEM_UNIT_OF_MEASURE_MIN_LENGTH })))
                }
                if (value.length > LINE_ITEM_UNIT_OF_MEASURE_MAX_LENGTH) {
                    return Promise.reject(new Error(i18n.t("validation:max.string", { max: LINE_ITEM_UNIT_OF_MEASURE_MAX_LENGTH })))
                }
                if (!isAllowedCompanyNameCharsOnly(value)) {
                    return Promise.reject(new Error(i18n.t("validation:invalid_characters")))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
})
